import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SHOWCASE_CONTENT, SHOWCASE_SLIDES } from "../data/showcase";

const DURATION = 5500;

export const Showcase = ({ lang }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % SHOWCASE_SLIDES.length), DURATION);
    return () => clearInterval(timer);
  }, [paused]);

  const slide = SHOWCASE_SLIDES[index];

  return (
    <section id="showcase" data-testid="showcase-section" className="scroll-mt-24 border-t border-white/10 px-5 py-24 sm:px-8 sm:py-32">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 max-w-2xl"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-400">{SHOWCASE_CONTENT.eyebrow[lang]}</span>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">{SHOWCASE_CONTENT.title[lang]}</h2>
          <p className="mt-4 text-base text-slate-400 sm:text-lg">{SHOWCASE_CONTENT.sub[lang]}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          className="gold-trace relative h-[420px] overflow-hidden rounded-2xl border border-white/10 sm:h-[540px]"
        >
          <AnimatePresence mode="sync">
            <motion.div
              key={slide.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.1, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <motion.img
                src={slide.image}
                alt={slide.label[lang]}
                initial={{ scale: 1.12 }}
                animate={{ scale: 1 }}
                transition={{ duration: DURATION / 1000 + 1.2, ease: "linear" }}
                className="h-full w-full object-cover"
              />
            </motion.div>
          </AnimatePresence>

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/25 to-transparent" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0A0D14]/60 via-transparent to-transparent" />

          <div className="absolute inset-x-0 bottom-0 p-7 sm:p-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5 }}
              >
                <span data-testid="showcase-slide-label" className="text-[11px] font-bold uppercase tracking-[0.3em] text-amber-400">
                  {String(index + 1).padStart(2, "0")} — {slide.label[lang]}
                </span>
                <p data-testid="showcase-slide-desc" className="font-display mt-2 max-w-lg text-xl font-semibold text-white sm:text-2xl">
                  {slide.desc[lang]}
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex gap-2">
              {SHOWCASE_SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  data-testid={`showcase-progress-${s.id}`}
                  onClick={() => setIndex(i)}
                  aria-label={s.label[lang]}
                  className="group h-6 flex-1"
                >
                  <div className="h-[3px] w-full overflow-hidden rounded-full bg-white/15">
                    {i === index ? (
                      <motion.div
                        key={`${s.id}-${index}`}
                        initial={{ width: "0%" }}
                        animate={{ width: paused ? "0%" : "100%" }}
                        transition={{ duration: DURATION / 1000, ease: "linear" }}
                        className="h-full bg-amber-400"
                      />
                    ) : (
                      <div className={`h-full ${i < index ? "w-full bg-amber-400/50" : "w-0"}`} />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
